<template>
  <div :class="componentClassName">
    <div class="a-vdxb-select-field__select">
      <vue-next-select
        v-if="options"
        v-model="currentItem"
        label-by="label"
        value-by="value"
        search-placeholder=""
        :options="options"
        :visible-options="visibleOptions"
        :min="1"
        :track-by="option => option.value"
        :placeholder="placeholder"
        :searchable="searchable"
        :close-on-select="true"
        :clear-on-select="true"
        :disabled="disabled"
        :tabindex="tabindex"
        :replace-instance-uid="id"
        open-direction="bottom"
        @keydown.space.prevent="handleKeySpace"
        @keydown.down="handleOpened"
        @keydown.up="handleOpened"
        @focus="handleOpened"
        @blur="handleClosed"
        @search:input="handleSearchInput"
        @search:focus="handleSearchFocus"
        @search:blur="handleSearchBlur"
        @update:model-value="handleModelValueUpdated"
      />
    </div>
    <div v-if="showArrow" class="a-vdxb-select-field__icon-container">
      <a-vdxb-icon icon="caret-down" :color="iconColor" />
    </div>
  </div>
</template>

<script>
import { mapModifiers } from '@/libs/component';
import AVdxbIcon from '@/components/atoms/vdxb-icon/index.vue';
import VueNextSelect from 'vue-next-select';
import SimpleBar from 'simplebar';
import { cloneDeep } from 'lodash';

export default {
  name: 'a-vdxb-select-field',
  components: {
    AVdxbIcon,
    VueNextSelect,
  },
  props: {
    class: {
      type: String,
      default: '',
    },
    placeholder: {
      type: [String, null],
      default: '',
    },
    modelValue: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      default: null,
    },
    showArrow: {
      type: Boolean,
      default: true,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    tabopen: {
      type: Number,
      default: null,
    },
  },
  emits: ['selected', 'change', 'update:modelValue', 'search'],
  data() {
    return {
      currentItem: null,
      currentValue: '',
      isOpened: false,
      tabindex: 0,
      searchInput: '',
    };
  },
  computed: {
    componentClassName() {
      const baseClassName = mapModifiers(
        'a-vdxb-select-field',
        this.disabled ? 'disabled' : '',
        this.isOpened ? 'opened' : '',
        this.currentValue ? 'has-value' : '',
        this.showArrow ? '' : 'no-arrow',
        this.searchable ? 'searchable' : ''
      );
      return `${baseClassName} ${this.class}`.trim();
    },
    disabled() {
      return this.$attrs.disabled;
    },
    iconColor() {
      return this.disabled ? 'black' : 'blue';
    },
    visibleOptions() {
      if (this.searchable) {
        const re = new RegExp(this.searchInput.replace(/[.*+?^${}()|[]\];/gi, '$&'));
        return this.options.filter(option => re.test(option.label));
      }
      return this.options;
    },
  },
  watch: {
    value(val, oldVal) {
      this.setCurrentValue(val);
    },
    modelValue: {
      handler(val, oldVal) {
        this.setCurrentValue(val);
      },
      deep: true,
    },
  },
  mounted() {
    if (this.tabopen === null) {
      if (!this.searchable) this.tabindex = -1;
    } else {
      this.tabindex = this.tabopen;
    }

    if (this.modelValue) {
      this.setCurrentValue(this.modelValue);
    }
    // attach data-simplebar to vue-dropdown
    const dd = this.$el.querySelector('.vue-dropdown');
    if (dd) {
      new SimpleBar(dd);
    }
  },
  methods: {
    setCurrentValue(val) {
      this.currentItem = val;
      this.currentValue = val;
    },
    handleOpened(event) {
      this.isOpened = true;
    },
    handleKeySpace(event) {
      if (this.isOpened) {
        const target = event.target;
        const attr = thisEle
          ? target.getAttribute('aria-activedescendant')
          : target.closest('.vue-select').getAttribute('aria-activedescendant');
        const thisEle = document.getElementById(attr);
        if (thisEle) thisEle.click();
        this.isOpened = false;
      } else this.isOpened = true;
    },
    handleClosed(event) {
      this.isOpened = false;
    },
    handleSearchInput(event) {
      this.searchInput = event.target.value;
      this.$emit('search', this.searchInput);
    },
    handleSearchFocus() {
      this.searchInput = '';
    },
    handleSearchBlur(event) {
      // check if searchInput is exists in options
      for (let i = 0; i < this.options.length; i += 1) {
        const item = this.options[i];
        if (item) {
          if (item.value === this.searchInput) {
            this.setCurrentValue(item.value);
            this.emitEvent();
            break;
          }
        }
      }
    },
    emitEvent() {
      this.$emit('update:modelValue', cloneDeep(this.currentValue));
      this.$emit('change', cloneDeep(this.currentValue));
    },
    handleModelValueUpdated(event) {
      this.currentValue = this.currentItem;
      this.emitEvent();
      this.handleClosed();
    },
  },
};
</script>
